import React, { useState, useEffect, useCallback } from 'react';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const TRIGGER_OPTIONS = [
  { value: 'ticket_created', label: 'Ticket Created', icon: 'Plus', group: 'Tickets', color: 'blue' },
  { value: 'ticket_status_changed', label: 'Ticket Status Changed', icon: 'RefreshCw', group: 'Tickets', color: 'blue' },
  { value: 'ticket_assigned', label: 'Ticket Assigned', icon: 'UserCheck', group: 'Tickets', color: 'blue' },
  { value: 'invoice_created', label: 'Invoice Created', icon: 'FileText', group: 'Finance', color: 'emerald' },
  { value: 'invoice_paid', label: 'Invoice Paid', icon: 'CheckCircle', group: 'Finance', color: 'emerald' },
  { value: 'invoice_overdue', label: 'Invoice Overdue', icon: 'AlertCircle', group: 'Finance', color: 'emerald' },
  { value: 'quote_approved', label: 'Quote Approved', icon: 'ThumbsUp', group: 'Sales', color: 'violet' },
  { value: 'quote_rejected', label: 'Quote Rejected', icon: 'ThumbsDown', group: 'Sales', color: 'violet' },
  { value: 'work_order_created', label: 'Work Order Created', icon: 'Wrench', group: 'Operations', color: 'amber' },
  { value: 'work_order_completed', label: 'Work Order Completed', icon: 'CheckSquare', group: 'Operations', color: 'amber' },
  { value: 'customer_created', label: 'Customer Created', icon: 'UserPlus', group: 'Customers', color: 'cyan' },
  { value: 'schedule_based', label: 'Schedule / Time-based', icon: 'Clock', group: 'System', color: 'slate' },
];

const ACTION_OPTIONS = [
  { value: 'send_email', label: 'Send Email', icon: 'Mail', description: 'Send an email to a recipient' },
  { value: 'send_notification', label: 'Send Notification', icon: 'Bell', description: 'Push notification or SMS' },
  { value: 'assign_technician', label: 'Assign Technician', icon: 'UserCog', description: 'Auto-assign to a technician' },
  { value: 'update_status', label: 'Update Status', icon: 'RefreshCw', description: 'Change record status' },
  { value: 'create_ticket', label: 'Create Ticket', icon: 'ClipboardList', description: 'Auto-create a new ticket' },
  { value: 'create_invoice', label: 'Create Invoice', icon: 'DollarSign', description: 'Generate an invoice' },
  { value: 'add_tag', label: 'Add Tag', icon: 'Tag', description: 'Tag the record' },
  { value: 'escalate', label: 'Escalate', icon: 'TrendingUp', description: 'Escalate to manager' },
  { value: 'webhook', label: 'Webhook', icon: 'Zap', description: 'Call an external URL' },
];

const CONDITION_OPERATORS = [
  { value: 'equals', label: 'equals' },
  { value: 'not_equals', label: 'does not equal' },
  { value: 'contains', label: 'contains' },
  { value: 'greater_than', label: 'is greater than' },
  { value: 'less_than', label: 'is less than' },
  { value: 'is_empty', label: 'is empty' },
  { value: 'is_not_empty', label: 'is not empty' },
];

const STATUS_COLORS = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  inactive: 'bg-gray-100 text-gray-600 border-gray-200',
  draft: 'bg-amber-50 text-amber-700 border-amber-200',
};

const TRIGGER_COLORS = {
  blue: 'bg-blue-50 text-blue-700',
  emerald: 'bg-emerald-50 text-emerald-700',
  violet: 'bg-violet-50 text-violet-700',
  amber: 'bg-amber-50 text-amber-700',
  cyan: 'bg-cyan-50 text-cyan-700',
  slate: 'bg-slate-100 text-slate-700',
};

const WorkflowModal = ({ workflow, onClose, onSave, orgId, userId }) => {
  const isEdit = !!workflow?.id;
  const [form, setForm] = useState({
    name: workflow?.name || '',
    description: workflow?.description || '',
    trigger_type: workflow?.trigger_type || 'ticket_created',
    trigger_config: workflow?.trigger_config || {},
    conditions: workflow?.conditions || [],
    actions: workflow?.actions || [],
    workflow_status: workflow?.workflow_status || 'draft',
  });
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);

  const addCondition = () => {
    setForm(f => ({ ...f, conditions: [...f?.conditions, { field: '', operator: 'equals', value: '' }] }));
  };

  const removeCondition = (i) => {
    setForm(f => ({ ...f, conditions: f?.conditions?.filter((_, idx) => idx !== i) }));
  };

  const updateCondition = (i, key, val) => {
    setForm(f => {
      const updated = [...f?.conditions];
      updated[i] = { ...updated?.[i], [key]: val };
      return { ...f, conditions: updated };
    });
  };

  const addAction = (type) => {
    const actionDef = ACTION_OPTIONS?.find(a => a?.value === type);
    setForm(f => ({ ...f, actions: [...f?.actions, { type, config: {}, label: actionDef?.label || type }] }));
  };

  const removeAction = (i) => {
    setForm(f => ({ ...f, actions: f?.actions?.filter((_, idx) => idx !== i) }));
  };

  const updateActionConfig = (i, key, val) => {
    setForm(f => {
      const updated = [...f?.actions];
      updated[i] = { ...updated?.[i], config: { ...updated?.[i]?.config, [key]: val } };
      return { ...f, actions: updated };
    });
  };

  const handleSave = async () => {
    if (!form?.name?.trim() || form?.actions?.length === 0) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        organization_id: orgId,
        updated_by: userId,
        updated_at: new Date()?.toISOString(),
      };
      if (!isEdit) payload.created_by = userId;

      let result;
      if (isEdit) {
        result = await supabase?.from('workflow_automations')?.update(payload)?.eq('id', workflow?.id);
      } else {
        result = await supabase?.from('workflow_automations')?.insert(payload);
      }
      if (result?.error) throw result?.error;
      onSave();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const selectedTrigger = TRIGGER_OPTIONS?.find(t => t?.value === form?.trigger_type);

  const steps = [
    { num: 1, label: 'Trigger' },
    { num: 2, label: 'Conditions' },
    { num: 3, label: 'Actions' },
    { num: 4, label: 'Review' },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{isEdit ? 'Edit Workflow' : 'Build Workflow'}</h2>
            <p className="text-sm text-gray-500 mt-1">Automate business processes with triggers and actions</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <Icon name="X" size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="px-6 py-3 border-b border-gray-100 flex items-center gap-2">
          {steps?.map((s, i) => (
            <React.Fragment key={s?.num}>
              <button
                onClick={() => setStep(s?.num)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  step === s?.num ? 'bg-blue-600 text-white' : step > s?.num ? 'bg-blue-50 text-blue-700' : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${
                  step === s?.num ? 'bg-white/20' : step > s?.num ? 'bg-blue-200' : 'bg-gray-200'
                }`}>{step > s?.num ? '✓' : s?.num}</span>
                {s?.label}
              </button>
              {i < steps?.length - 1 && <Icon name="ChevronRight" size={14} className="text-gray-300" />}
            </React.Fragment>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Trigger */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Workflow Name *</label>
                  <Input value={form?.name} onChange={e => setForm(f => ({ ...f, name: e?.target?.value }))} placeholder="e.g. New Ticket Notification" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={form?.workflow_status}
                    onChange={e => setForm(f => ({ ...f, workflow_status: e?.target?.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <Input value={form?.description} onChange={e => setForm(f => ({ ...f, description: e?.target?.value }))} placeholder="What does this workflow do?" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Select Trigger Event *</label>
                {['Tickets', 'Finance', 'Sales', 'Operations', 'Customers', 'System']?.map(group => (
                  <div key={group} className="mb-4">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">{group}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {TRIGGER_OPTIONS?.filter(t => t?.group === group)?.map(trigger => (
                        <button
                          key={trigger?.value}
                          onClick={() => setForm(f => ({ ...f, trigger_type: trigger?.value }))}
                          className={`flex items-center gap-2 p-3 rounded-xl border-2 text-left transition-all ${
                            form?.trigger_type === trigger?.value
                              ? 'border-blue-500 bg-blue-50' :'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            form?.trigger_type === trigger?.value ? 'bg-blue-100' : 'bg-gray-100'
                          }`}>
                            <Icon name={trigger?.icon} size={15} className={form?.trigger_type === trigger?.value ? 'text-blue-600' : 'text-gray-500'} />
                          </div>
                          <span className={`text-sm font-medium ${
                            form?.trigger_type === trigger?.value ? 'text-blue-700' : 'text-gray-700'
                          }`}>{trigger?.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Conditions */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-gray-900">Conditions (Optional)</h3>
                  <p className="text-sm text-gray-500">Add conditions to filter when this workflow runs</p>
                </div>
                <Button variant="outline" onClick={addCondition} className="flex items-center gap-1.5 text-sm">
                  <Icon name="Plus" size={14} /> Add Condition
                </Button>
              </div>

              {form?.conditions?.length === 0 ? (
                <div className="text-center py-10 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                  <Icon name="Filter" size={28} className="text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500 text-sm">No conditions — workflow runs on every trigger event</p>
                  <button onClick={addCondition} className="mt-2 text-blue-600 text-sm hover:underline">Add a condition</button>
                </div>
              ) : (
                <div className="space-y-3">
                  {form?.conditions?.map((cond, i) => (
                    <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-xl p-3">
                      {i > 0 && <span className="text-xs font-semibold text-gray-400 w-8 text-center">AND</span>}
                      {i === 0 && <span className="text-xs font-semibold text-gray-400 w-8 text-center">IF</span>}
                      <input
                        type="text"
                        value={cond?.field}
                        onChange={e => updateCondition(i, 'field', e?.target?.value)}
                        placeholder="Field (e.g. priority)"
                        className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      />
                      <select
                        value={cond?.operator}
                        onChange={e => updateCondition(i, 'operator', e?.target?.value)}
                        className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                      >
                        {CONDITION_OPERATORS?.map(op => <option key={op?.value} value={op?.value}>{op?.label}</option>)}
                      </select>
                      {!['is_empty', 'is_not_empty']?.includes(cond?.operator) && (
                        <input
                          type="text"
                          value={cond?.value}
                          onChange={e => updateCondition(i, 'value', e?.target?.value)}
                          placeholder="Value"
                          className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                        />
                      )}
                      <button onClick={() => removeCondition(i)} className="p-2 rounded-lg hover:bg-red-50 hover:text-red-600 text-gray-400 transition-colors">
                        <Icon name="X" size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Actions */}
          {step === 3 && (
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-gray-900">Actions</h3>
                <p className="text-sm text-gray-500">Define what happens when the trigger fires</p>
              </div>

              <div className="grid grid-cols-3 gap-2">
                {ACTION_OPTIONS?.map(action => (
                  <button
                    key={action?.value}
                    onClick={() => addAction(action?.value)}
                    className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all text-center group"
                  >
                    <div className="w-9 h-9 bg-gray-100 group-hover:bg-blue-100 rounded-lg flex items-center justify-center transition-colors">
                      <Icon name={action?.icon} size={16} className="text-gray-500 group-hover:text-blue-600" />
                    </div>
                    <span className="text-xs font-medium text-gray-700 group-hover:text-blue-700">{action?.label}</span>
                  </button>
                ))}
              </div>

              {form?.actions?.length > 0 && (
                <div className="space-y-3 mt-4">
                  <p className="text-sm font-medium text-gray-700">Configured Actions ({form?.actions?.length})</p>
                  {form?.actions?.map((action, i) => {
                    const actionDef = ACTION_OPTIONS?.find(a => a?.value === action?.type);
                    return (
                      <div key={i} className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 bg-blue-100 rounded-lg flex items-center justify-center">
                              <Icon name={actionDef?.icon || 'Zap'} size={13} className="text-blue-600" />
                            </div>
                            <span className="text-sm font-semibold text-gray-800">{actionDef?.label}</span>
                            <span className="text-xs text-gray-400">#{i + 1}</span>
                          </div>
                          <button onClick={() => removeAction(i)} className="p-1.5 rounded-lg hover:bg-red-50 hover:text-red-600 text-gray-400 transition-colors">
                            <Icon name="Trash2" size={13} />
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {action?.type === 'send_email' && (
                            <>
                              <div>
                                <label className="text-xs text-gray-500 mb-1 block">Recipient</label>
                                <input type="text" value={action?.config?.recipient || ''} onChange={e => updateActionConfig(i, 'recipient', e?.target?.value)} placeholder="customer / technician" className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-blue-400" />
                              </div>
                              <div>
                                <label className="text-xs text-gray-500 mb-1 block">Subject</label>
                                <input type="text" value={action?.config?.subject || ''} onChange={e => updateActionConfig(i, 'subject', e?.target?.value)} placeholder="Email subject" className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-blue-400" />
                              </div>
                              <div className="col-span-2">
                                <label className="text-xs text-gray-500 mb-1 block">Message</label>
                                <textarea value={action?.config?.message || ''} onChange={e => updateActionConfig(i, 'message', e?.target?.value)} rows={2} placeholder="Email body..." className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 resize-none" />
                              </div>
                            </>
                          )}
                          {action?.type === 'send_notification' && (
                            <>
                              <div>
                                <label className="text-xs text-gray-500 mb-1 block">Recipient</label>
                                <input type="text" value={action?.config?.recipient || ''} onChange={e => updateActionConfig(i, 'recipient', e?.target?.value)} placeholder="customer / technician" className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-blue-400" />
                              </div>
                              <div>
                                <label className="text-xs text-gray-500 mb-1 block">Channel</label>
                                <select value={action?.config?.channel || 'email'} onChange={e => updateActionConfig(i, 'channel', e?.target?.value)} className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-blue-400">
                                  <option value="email">Email</option>
                                  <option value="sms">SMS</option>
                                  <option value="push">Push</option>
                                </select>
                              </div>
                              <div className="col-span-2">
                                <label className="text-xs text-gray-500 mb-1 block">Message</label>
                                <input type="text" value={action?.config?.message || ''} onChange={e => updateActionConfig(i, 'message', e?.target?.value)} placeholder="Notification message" className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-blue-400" />
                              </div>
                            </>
                          )}
                          {action?.type === 'update_status' && (
                            <div>
                              <label className="text-xs text-gray-500 mb-1 block">New Status</label>
                              <input type="text" value={action?.config?.new_status || ''} onChange={e => updateActionConfig(i, 'new_status', e?.target?.value)} placeholder="e.g. resolved" className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-blue-400" />
                            </div>
                          )}
                          {action?.type === 'escalate' && (
                            <>
                              <div>
                                <label className="text-xs text-gray-500 mb-1 block">Escalate To</label>
                                <input type="text" value={action?.config?.escalate_to || ''} onChange={e => updateActionConfig(i, 'escalate_to', e?.target?.value)} placeholder="manager / admin" className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-blue-400" />
                              </div>
                              <div>
                                <label className="text-xs text-gray-500 mb-1 block">Priority</label>
                                <select value={action?.config?.priority || 'high'} onChange={e => updateActionConfig(i, 'priority', e?.target?.value)} className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-blue-400">
                                  <option value="low">Low</option>
                                  <option value="medium">Medium</option>
                                  <option value="high">High</option>
                                  <option value="critical">Critical</option>
                                </select>
                              </div>
                            </>
                          )}
                          {action?.type === 'webhook' && (
                            <div className="col-span-2">
                              <label className="text-xs text-gray-500 mb-1 block">Webhook URL</label>
                              <input type="url" value={action?.config?.url || ''} onChange={e => updateActionConfig(i, 'url', e?.target?.value)} placeholder="https://..." className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-blue-400" />
                            </div>
                          )}
                          {action?.type === 'add_tag' && (
                            <div>
                              <label className="text-xs text-gray-500 mb-1 block">Tag Name</label>
                              <input type="text" value={action?.config?.tag || ''} onChange={e => updateActionConfig(i, 'tag', e?.target?.value)} placeholder="e.g. urgent" className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-blue-400" />
                            </div>
                          )}
                          {['assign_technician', 'create_ticket', 'create_invoice']?.includes(action?.type) && (
                            <div className="col-span-2">
                              <p className="text-xs text-gray-400 italic">Configuration for this action will be applied based on current context.</p>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Step 4: Review */}
          {step === 4 && (
            <div className="space-y-4">
              <h3 className="font-medium text-gray-900">Review Workflow</h3>
              <div className="bg-gray-50 rounded-xl p-5 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Icon name="Zap" size={18} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{form?.name || 'Unnamed Workflow'}</p>
                    <p className="text-sm text-gray-500">{form?.description || 'No description'}</p>
                  </div>
                  <span className={`ml-auto text-xs px-2.5 py-1 rounded-full border font-medium capitalize ${STATUS_COLORS?.[form?.workflow_status]}`}>
                    {form?.workflow_status}
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <p className="text-xs font-semibold text-gray-500 mb-1">TRIGGER</p>
                    <p className="text-sm font-medium text-gray-800">{TRIGGER_OPTIONS?.find(t => t?.value === form?.trigger_type)?.label}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <p className="text-xs font-semibold text-gray-500 mb-1">CONDITIONS</p>
                    <p className="text-sm font-medium text-gray-800">{form?.conditions?.length} condition{form?.conditions?.length !== 1 ? 's' : ''}</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-gray-200">
                    <p className="text-xs font-semibold text-gray-500 mb-1">ACTIONS</p>
                    <p className="text-sm font-medium text-gray-800">{form?.actions?.length} action{form?.actions?.length !== 1 ? 's' : ''}</p>
                  </div>
                </div>

                {form?.actions?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 mb-2">ACTION SEQUENCE</p>
                    <div className="space-y-2">
                      {form?.actions?.map((action, i) => {
                        const def = ACTION_OPTIONS?.find(a => a?.value === action?.type);
                        return (
                          <div key={i} className="flex items-center gap-2 bg-white rounded-lg p-2.5 border border-gray-200">
                            <span className="w-5 h-5 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-bold">{i + 1}</span>
                            <Icon name={def?.icon || 'Zap'} size={14} className="text-gray-500" />
                            <span className="text-sm text-gray-700">{def?.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              {form?.actions?.length === 0 && (
                <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-700 px-4 py-3 rounded-lg text-sm">
                  <Icon name="AlertTriangle" size={15} />
                  At least one action is required to save this workflow.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
          <Button variant="outline" onClick={() => step > 1 ? setStep(s => s - 1) : onClose}>
            {step > 1 ? 'Back' : 'Cancel'}
          </Button>
          <div className="flex gap-3">
            {step < 4 ? (
              <Button onClick={() => setStep(s => s + 1)} disabled={step === 1 && !form?.name?.trim()}>
                Next <Icon name="ChevronRight" size={15} />
              </Button>
            ) : (
              <Button onClick={handleSave} disabled={saving || form?.actions?.length === 0}>
                {saving ? 'Saving...' : isEdit ? 'Update Workflow' : 'Activate Workflow'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const WorkflowAutomationCenter = () => {
  const { orgMembership, user } = useAuth();
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editWorkflow, setEditWorkflow] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const orgId = orgMembership?.organization_id;
  const userId = user?.id;

  const showSuccess = (msg) => { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(''), 3000); };
  const showError = (msg) => { setErrorMsg(msg); setTimeout(() => setErrorMsg(''), 4000); };

  const fetchWorkflows = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('workflow_automations')?.select('*')?.eq('organization_id', orgId)?.order('created_at', { ascending: false });
      if (error) throw error;
      setWorkflows(data || []);
    } catch (err) {
      showError(err?.message || 'Failed to load workflows');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { fetchWorkflows(); }, [fetchWorkflows]);

  const handleDelete = async (id) => {
    try {
      const { error } = await supabase?.from('workflow_automations')?.delete()?.eq('id', id);
      if (error) throw error;
      setWorkflows(prev => prev?.filter(w => w?.id !== id));
      setDeleteConfirm(null);
      showSuccess('Workflow deleted');
    } catch (err) {
      showError(err?.message || 'Failed to delete workflow');
    }
  };

  const handleToggleStatus = async (workflow) => {
    const newStatus = workflow?.workflow_status === 'active' ? 'inactive' : 'active';
    try {
      const { error } = await supabase?.from('workflow_automations')?.update({ workflow_status: newStatus, updated_at: new Date()?.toISOString() })?.eq('id', workflow?.id);
      if (error) throw error;
      setWorkflows(prev => prev?.map(w => w?.id === workflow?.id ? { ...w, workflow_status: newStatus } : w));
      showSuccess(`Workflow ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
    } catch (err) {
      showError(err?.message || 'Failed to update workflow');
    }
  };

  const filtered = workflows?.filter(w => {
    const matchStatus = activeFilter === 'all' || w?.workflow_status === activeFilter;
    const matchSearch = !search || w?.name?.toLowerCase()?.includes(search?.toLowerCase()) || w?.description?.toLowerCase()?.includes(search?.toLowerCase());
    return matchStatus && matchSearch;
  });

  const stats = {
    total: workflows?.length,
    active: workflows?.filter(w => w?.workflow_status === 'active')?.length,
    draft: workflows?.filter(w => w?.workflow_status === 'draft')?.length,
    totalRuns: workflows?.reduce((sum, w) => sum + (w?.run_count || 0), 0),
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Workflow Automation</h1>
              <p className="text-gray-500 mt-1">Build automated workflows with triggers, conditions, and actions</p>
            </div>
            <Button onClick={() => { setEditWorkflow(null); setShowModal(true); }} className="flex items-center gap-2">
              <Icon name="Plus" size={16} />
              New Workflow
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Total Workflows', value: stats?.total, icon: 'Zap', color: 'blue' },
              { label: 'Active', value: stats?.active, icon: 'CheckCircle', color: 'emerald' },
              { label: 'Drafts', value: stats?.draft, icon: 'FileText', color: 'amber' },
              { label: 'Total Executions', value: stats?.totalRuns?.toLocaleString(), icon: 'Activity', color: 'violet' },
            ]?.map(stat => (
              <div key={stat?.label} className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm text-gray-500">{stat?.label}</p>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-${stat?.color}-50`}>
                    <Icon name={stat?.icon} size={15} className={`text-${stat?.color}-600`} />
                  </div>
                </div>
                <p className="text-2xl font-bold text-gray-900">{stat?.value}</p>
              </div>
            ))}
          </div>

          {/* Alerts */}
          {successMsg && (
            <div className="mb-4 flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-lg text-sm">
              <Icon name="CheckCircle" size={16} />{successMsg}
            </div>
          )}
          {errorMsg && (
            <div className="mb-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              <Icon name="AlertCircle" size={16} />{errorMsg}
            </div>
          )}

          {/* Filters & Search */}
          <div className="flex items-center gap-3 mb-5">
            <div className="relative flex-1 max-w-sm">
              <Icon name="Search" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e?.target?.value)}
                placeholder="Search workflows..."
                className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              />
            </div>
            <div className="flex gap-1 bg-white border border-gray-200 rounded-lg p-1">
              {['all', 'active', 'inactive', 'draft']?.map(f => (
                <button
                  key={f}
                  onClick={() => setActiveFilter(f)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium capitalize transition-colors ${
                    activeFilter === f ? 'bg-blue-600 text-white' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          {/* Workflow List */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : filtered?.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-xl border border-gray-200">
              <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Zap" size={24} className="text-gray-400" />
              </div>
              <h3 className="text-gray-700 font-medium mb-1">No workflows found</h3>
              <p className="text-gray-400 text-sm mb-4">{search ? 'Try a different search' : 'Build your first automation workflow'}</p>
              {!search && <Button onClick={() => { setEditWorkflow(null); setShowModal(true); }}>Create Workflow</Button>}
            </div>
          ) : (
            <div className="space-y-3">
              {filtered?.map(workflow => {
                const triggerDef = TRIGGER_OPTIONS?.find(t => t?.value === workflow?.trigger_type);
                const actionsCount = workflow?.actions?.length || 0;
                const conditionsCount = workflow?.conditions?.length || 0;
                return (
                  <div key={workflow?.id} className="bg-white rounded-xl border border-gray-200 hover:border-blue-200 hover:shadow-sm transition-all group">
                    <div className="p-5">
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${triggerDef ? TRIGGER_COLORS?.[triggerDef?.color] : 'bg-gray-100 text-gray-600'}`}>
                          <Icon name={triggerDef?.icon || 'Zap'} size={18} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <h3 className="font-semibold text-gray-900">{workflow?.name}</h3>
                            <span className={`text-xs px-2 py-0.5 rounded-full border font-medium capitalize ${STATUS_COLORS?.[workflow?.workflow_status]}`}>
                              {workflow?.workflow_status}
                            </span>
                          </div>
                          {workflow?.description && (
                            <p className="text-sm text-gray-500 mb-2">{workflow?.description}</p>
                          )}
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <Icon name="Zap" size={11} />
                              {triggerDef?.label || workflow?.trigger_type}
                            </span>
                            <span className="flex items-center gap-1">
                              <Icon name="Filter" size={11} />
                              {conditionsCount} condition{conditionsCount !== 1 ? 's' : ''}
                            </span>
                            <span className="flex items-center gap-1">
                              <Icon name="Play" size={11} />
                              {actionsCount} action{actionsCount !== 1 ? 's' : ''}
                            </span>
                            <span className="flex items-center gap-1">
                              <Icon name="Activity" size={11} />
                              {workflow?.run_count || 0} runs
                            </span>
                            {workflow?.last_run_at && (
                              <span className="flex items-center gap-1">
                                <Icon name="Clock" size={11} />
                                Last: {new Date(workflow.last_run_at)?.toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => handleToggleStatus(workflow)}
                            className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${
                              workflow?.workflow_status === 'active'
                                ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                            }`}
                          >
                            <Icon name={workflow?.workflow_status === 'active' ? 'Pause' : 'Play'} size={12} />
                            {workflow?.workflow_status === 'active' ? 'Pause' : 'Activate'}
                          </button>
                          <button
                            onClick={() => { setEditWorkflow(workflow); setShowModal(true); }}
                            className="p-2 rounded-lg hover:bg-blue-50 hover:text-blue-600 text-gray-400 transition-colors"
                          >
                            <Icon name="Pencil" size={14} />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(workflow?.id)}
                            className="p-2 rounded-lg hover:bg-red-50 hover:text-red-600 text-gray-400 transition-colors"
                          >
                            <Icon name="Trash2" size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {showModal && (
        <WorkflowModal
          workflow={editWorkflow}
          onClose={() => { setShowModal(false); setEditWorkflow(null); }}
          onSave={() => { setShowModal(false); setEditWorkflow(null); fetchWorkflows(); showSuccess(editWorkflow ? 'Workflow updated!' : 'Workflow created!'); }}
          orgId={orgId}
          userId={userId}
        />
      )}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Icon name="Trash2" size={18} className="text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Delete Workflow</h3>
                <p className="text-sm text-gray-500">This action cannot be undone.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowAutomationCenter;
